import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./CorporateValue.css";
import Testimonial from "../../../Testimonial/Testimonial";

const CorporateValue = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  const values = [
    // ... (previous values remain the same)
    {
      title: "Innovation",
      description:
        "We push boundaries and explore new possibilities in technology to deliver cutting-edge solutions.",
      icon: "💡",
    },
    // ... (all previous values)
    {
      title: "Work-Life Balance",
      description:
        "We promote a healthy balance between professional commitments and personal well-being.",
      icon: "⚖️",
    },
    // NEW VALUES ADDED BELOW
    {
      title: "Quality First",
      description:
        "We never compromise on quality, ensuring our solutions meet the highest standards of performance.",
      icon: "🏆",
    },
    {
      title: "Ownership Mindset",
      description:
        "We take personal responsibility for the success of our projects and the satisfaction of our clients.",
      icon: "🔑",
    },
    {
      title: "Data-Driven Decisions",
      description:
        "We leverage analytics and insights to make informed choices that drive business success.",
      icon: "📊",
    },
    {
      title: "Global Perspective",
      description:
        "We think beyond borders to create solutions that work in diverse markets and cultures.",
      icon: "🗺️",
    },
    {
      title: "Trust",
      description:
        "We build relationships based on reliability, consistency, and mutual respect.",
      icon: "🤲",
    },
    {
      title: "Efficiency",
      description:
        "We optimize processes and eliminate waste to deliver maximum value with minimum resources.",
      icon: "⏱️",
    },
    {
      title: "Resilience",
      description:
        "We persevere through challenges and emerge stronger from setbacks.",
      icon: "🌊",
    },
    {
      title: "Open Communication",
      description:
        "We foster an environment where honest dialogue and feedback are encouraged and valued.",
      icon: "🗣️",
    },
    {
      title: "Ethical Technology",
      description:
        "We develop solutions that respect privacy, promote fairness, and benefit society.",
      icon: "⚖️",
    },
    {
      title: "Client Partnership",
      description:
        "We view our clients as true partners, working together to achieve shared goals.",
      icon: "🤝",
    },
  ];

  return (
    <div>
      {/* ✅ SEO Meta Tags */}
      <Helmet>
        <title>
          Corporate Values & Technology Stack | AI, Blockchain, Web & Mobile
          Development
        </title>
        <meta
          name="description"
          content="Explore our corporate values and advanced technology stack including AI, Blockchain, IoT, Web, and Mobile app development with tools like React, Angular, Node.js, Laravel, and more."
        />
        <meta
          name="keywords"
          content="Corporate values, technology stack, React development, Angular.js, Node.js, Laravel, Blockchain, Solidity, Ethereum, Hyperledger, IoT, AI, Artificial Intelligence, Machine Learning, Data Science, Flutter, Ionic, Android, iOS, PHP, MySQL, PostgreSQL, MongoDB, SQL Server, Oracle, Salesforce, Django, CodeIgniter, Bootstrap, CSS3, HTML5, JavaScript, jQuery, Sass, Less, Web Development, Mobile App Development, Cloud Computing, Virtual Reality, Augmented Reality, GoChain, ERC-20, OneLedger, Appian"
        />
      </Helmet>
      <div className="mkValues__container">
        <div className="mkValues__hero">
          <h1 className="mkValues__title mkValues__animate-fadein">
            Our Corporate Values
          </h1>
          <p className="mkValues__subtitle mkValues__animate-fadein-delay">
            The principles that guide everything we do at MK E-Tech
          </p>
        </div>

        <div className="mkValues__grid">
          {values.map((value, index) => (
            <div
              key={index}
              className={`mkValues__card mkValues__animate-slideup-${index}`}
            >
              <div className="mkValues__icon-container">
                <span className="mkValues__icon">{value.icon}</span>
              </div>
              <h3 className="mkValues__card-title">{value.title}</h3>
              <p className="mkValues__card-description">{value.description}</p>
            </div>
          ))}
        </div>

        <div className="mkValues__commitment">
          <h2 className="mkValues__commitment-title">Our Commitment to You</h2>
          <p className="mkValues__commitment-text">
            At MK E-Tech, our values aren't just words on a page - they're the
            foundation of our culture and the guiding principles for every
            decision we make. We're committed to upholding these values as we
            work together to innovate, excel, and deliver exceptional software
            solutions to our clients around the world.
          </p>

          <div className="mkValues__commitment-points">
            <div className="mkValues__commitment-item">
              <h3 className="mkValues__commitment-subtitle">To Our Clients</h3>
              <p>
                We pledge to understand your unique challenges and deliver
                tailored solutions that drive your business forward. Your
                success is our most important metric.
              </p>
            </div>

            <div className="mkValues__commitment-item">
              <h3 className="mkValues__commitment-subtitle">To Our Team</h3>
              <p>
                We promise to foster an environment where every team member can
                grow, innovate, and find fulfillment in their work. We invest in
                continuous learning and professional development.
              </p>
            </div>

            <div className="mkValues__commitment-item">
              <h3 className="mkValues__commitment-subtitle">
                To Our Community
              </h3>
              <p>
                We commit to being responsible corporate citizens, contributing
                positively to the communities where we operate and using
                technology to address social challenges.
              </p>
            </div>

            <div className="mkValues__commitment-item">
              <h3 className="mkValues__commitment-subtitle">To Our Planet</h3>
              <p>
                We vow to develop sustainable solutions and adopt
                environmentally conscious practices that minimize our ecological
                footprint for future generations.
              </p>
            </div>

            {/* NEW COMMITMENT ITEMS ADDED BELOW */}
            <div className="mkValues__commitment-item">
              <h3 className="mkValues__commitment-subtitle">To Innovation</h3>
              <p>
                We dedicate ourselves to pushing technological boundaries,
                exploring emerging technologies, and fostering a culture of
                creative problem-solving that delivers cutting-edge solutions.
              </p>
            </div>

            <div className="mkValues__commitment-item">
              <h3 className="mkValues__commitment-subtitle">To Quality</h3>
              <p>
                We pledge to maintain the highest standards in all our
                deliverables, implementing rigorous testing protocols and
                quality assurance processes that ensure reliability and
                excellence.
              </p>
            </div>

            <div className="mkValues__commitment-item">
              <h3 className="mkValues__commitment-subtitle">
                To Security & Privacy
              </h3>
              <p>
                We vow to protect our clients' data with state-of-the-art
                security measures, maintaining strict confidentiality and
                complying with all regulatory requirements to ensure trust and
                safety.
              </p>
            </div>

            <div className="mkValues__commitment-item">
              <h3 className="mkValues__commitment-subtitle">
                To Continuous Improvement
              </h3>
              <p>
                We commit to regularly evaluating and enhancing our processes,
                methodologies, and services based on feedback and changing
                market dynamics to deliver ever-increasing value.
              </p>
            </div>
              <div className="mkValues__commitment-item">
    <h3 className="mkValues__commitment-subtitle">To Ethical Practices</h3>
    <p>
      We promise to uphold the highest ethical standards in all our business dealings, 
      ensuring fairness, transparency, and integrity in every interaction with stakeholders, 
      partners, and competitors.
    </p>
  </div>
          </div>

          <div className="mkValues__commitment-pledge">
            <h3 className="mkValues__pledge-title">Our Pledge</h3>
            <p className="mkValues__pledge-text">
              Every day, we recommit to these values through our actions, our
              decisions, and our work. They are the compass that guides our
              journey and the standard by which we measure our success. When you
              partner with MK E-Tech, you can trust that these principles will
              be reflected in every interaction and every solution we deliver.
            </p>
          </div>
        </div>
      </div>
      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateValue;
